/**
 * persistence/db/database.js
 *
 * Dexie singleton — the single IndexedDB connection used by the entire
 * persistence layer.  Import `db` from this file; never instantiate Dexie
 * directly elsewhere.
 *
 * Why a singleton?
 *   IndexedDB allows only one open upgrade transaction at a time.  Multiple
 *   Dexie instances pointing at the same database cause "blocked" upgrade
 *   events and data corruption.  A module-level singleton is safe because
 *   JavaScript modules are cached after the first import.
 *
 * Safari note:
 *   Safari's IDB implementation has historically had issues with:
 *     - Private browsing (throws QuotaExceededError — we catch it gracefully)
 *     - Version change transactions timing out (Dexie retries internally)
 *   Dexie 4.x handles both cases.  We add an extra open() check at the bottom
 *   to surface clear errors instead of silent failures.
 */

import Dexie from "dexie";
import { DB_NAME, DB_VERSION } from "../config";
import { getSchemaForVersion } from "./schema";
import {
  encStr, decStr, encBytes, decBytes, encJSON, decJSON,
} from "../crypto/fieldCipher";

// ─── Dexie instance ───────────────────────────────────────────────────────────

const db = new Dexie(DB_NAME);

// Register every schema version up to DB_VERSION, in order.
// Dexie applies them sequentially; new (additive) stores in v2 require no
// data migration, so no upgrade() callback is needed.
for (let v = 1; v <= DB_VERSION; v++) {
  db.version(v).stores(getSchemaForVersion(v));
}

// ─── At-rest field encryption ───────────────────────────────────────────────
// Transparently obfuscates the analyst string data so it isn't readable in
// DevTools. NONE of these fields are indexed, so encrypting them is safe for
// queries. Reads of legacy plaintext rows pass through unchanged (see cipher).
//
// fields: { str: [...], bytes: [...], json: [...] }
function protectFields(table, fields) {
  const { str = [], bytes = [], json = [] } = fields;

  table.hook("creating", (_pk, obj) => {
    str.forEach((f) => { if (obj[f] != null) obj[f] = encStr(obj[f]); });
    bytes.forEach((f) => { if (obj[f] != null) obj[f] = encBytes(obj[f]); });
    json.forEach((f) => { if (obj[f] != null) obj[f] = encJSON(obj[f]); });
  });

  table.hook("updating", (mods) => {
    const patch = {};
    str.forEach((f) => { if (f in mods && mods[f] != null) patch[f] = encStr(mods[f]); });
    bytes.forEach((f) => { if (f in mods && mods[f] != null) patch[f] = encBytes(mods[f]); });
    json.forEach((f) => { if (f in mods && mods[f] != null) patch[f] = encJSON(mods[f]); });
    return Object.keys(patch).length ? patch : undefined;
  });

  table.hook("reading", (obj) => {
    if (!obj) return obj;
    const out = { ...obj };
    str.forEach((f) => { if (out[f] != null) out[f] = decStr(out[f]); });
    bytes.forEach((f) => { if (out[f] != null) out[f] = decBytes(out[f]); });
    json.forEach((f) => { if (out[f] != null) out[f] = decJSON(out[f]); });
    return out;
  });
}

protectFields(db.sessions,         { str: ["currentString"] });
protectFields(db.syncQueue,        { str: ["string"] });
protectFields(db.versions,         { str: ["currentString"] });
protectFields(db.diffLog,          { json: ["diff"] });
protectFields(db.branches,         { bytes: ["stateUpdate"] });
protectFields(db.commits,          { bytes: ["stateUpdate"] });
protectFields(db.reviewerVersions, { json: ["eventStrings"] });

// ─── Typed table accessors ────────────────────────────────────────────────────
// Dexie infers table types from the schema string, but explicit accessors
// provide IDE autocomplete without TypeScript.

export const sessionsTable      = () => db.sessions;
export const versionsTable      = () => db.versions;
export const diffLogTable       = () => db.diffLog;
export const syncQueueTable     = () => db.syncQueue;
export const firestoreQueueTable = () => db.firestoreQueue;
export const branchesTable      = () => db.branches; // v2 — VCS layer
export const commitsTable       = () => db.commits;  // v2 — VCS layer
export const reviewerVersionsTable = () => db.reviewerVersions; // v3 — reviewer snapshots

// ─── Health check ─────────────────────────────────────────────────────────────

/**
 * Verify the database can be opened.  Called once at app startup from the
 * persistence middleware.  On Safari private mode, this will throw and the
 * SyncManager will gracefully fall back to REST-only mode.
 *
 * @returns {Promise<boolean>} true if IndexedDB is available and working.
 */
export async function checkDatabaseHealth() {
  try {
    await db.open();
    return true;
  } catch (error) {
    console.warn(
      "[persistence] IndexedDB unavailable — running in REST-only mode.",
      error.message
    );
    return false;
  }
}

/**
 * Closes the database connection.  Only needed in test environments.
 * The browser handles teardown automatically on page unload.
 */
export async function closeDatabase() {
  if (db.isOpen()) {
    db.close();
  }
}

/**
 * Wipes all locally-persisted data (every object store).  Call on logout so a
 * shared machine doesn't leave one analyst's tagging/session data readable in
 * the next user's IndexedDB.  Best-effort — failures are swallowed.
 */
export async function clearAllPersistence() {
  try {
    await Promise.all(db.tables.map((t) => t.clear()));
  } catch (error) {
    console.warn("[persistence] clearAllPersistence failed:", error.message);
  }
}

export default db;
